import React, { useState } from 'react';
import { Search, X, ChevronDown, ChevronUp, Filter } from 'lucide-react';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { COUNTRIES } from '../../utils/countries';

const getCountryName = (code) => {
  const c = COUNTRIES?.find(c => c.code === code);
  return c ? c.name : code;
};

const FilterSelect = ({ label, value, onChange, options, displayFn, testId, open, onOpenChange }) => (
  <div className="min-w-[140px]" data-testid={testId}>
    <Select value={value || '__all__'} onValueChange={v => onChange(v === '__all__' ? '' : v)} open={open} onOpenChange={onOpenChange}>
      <SelectTrigger className="h-8 text-xs">
        <SelectValue placeholder={label} />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="__all__">{label}</SelectItem>
        {options.map(o => (
          <SelectItem key={o} value={o}>{displayFn ? displayFn(o) : o}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  </div>
);

/**
 * Division7FilterBar — shared filter UI for all Division 7 listing pages.
 *
 * Props:
 *   filters        – current filter state object
 *   setFilter      – (field, value) => void
 *   clearFilters   – () => void
 *   filterOptions  – { [field]: string[] }
 *   schema         – item-specific filter definitions
 *   activeCount    – number of active filters
 *   testIdPrefix   – e.g. 'ti', 'ab', 'wb'
 */
export const Division7FilterBar = ({
  filters, setFilter, clearFilters, filterOptions, schema, activeCount, testIdPrefix = 'div7',
}) => {
  const [expanded, setExpanded] = useState(false);
  const [openDropdownId, setOpenDropdownId] = useState(null);

  const hasItemFilters = schema.length > 0;

  const dropdownProps = (id) => ({
    open: openDropdownId === id,
    onOpenChange: (isOpen) => setOpenDropdownId(isOpen ? id : null),
  });

  return (
    <div className="mb-4 space-y-2" data-testid={`${testIdPrefix}-filter-bar`}>
      {/* Row 1: Core filters */}
      <div className="flex flex-wrap items-center gap-2">
        {/* Search */}
        <div className="relative min-w-[200px] flex-1 max-w-xs">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
          <Input
            value={filters.search || ''}
            onChange={e => setFilter('search', e.target.value)}
            placeholder="Search product name or model"
            className="pl-8 h-8 text-xs"
            data-testid={`${testIdPrefix}-filter-search`}
          />
        </div>
        {/* Manufacturer */}
        <FilterSelect
          label="Manufacturer"
          value={filters.manufacturerName}
          onChange={v => setFilter('manufacturerName', v)}
          options={filterOptions.manufacturerName || []}
          testId={`${testIdPrefix}-filter-manufacturer`}
          {...dropdownProps('manufacturer')}
        />
        {/* Country */}
        <FilterSelect
          label="Country"
          value={filters.countryCode}
          onChange={v => setFilter('countryCode', v)}
          options={filterOptions.countryCode || []}
          displayFn={getCountryName}
          testId={`${testIdPrefix}-filter-country`}
          {...dropdownProps('country')}
        />
        {/* Status */}
        <FilterSelect
          label="Status"
          value={filters.status}
          onChange={v => setFilter('status', v)}
          options={['active', 'draft']}
          displayFn={v => v.charAt(0).toUpperCase() + v.slice(1)}
          testId={`${testIdPrefix}-filter-status`}
          {...dropdownProps('status')}
        />
        {/* Has Submittals */}
        <FilterSelect
          label="Has Submittals"
          value={filters.hasSubmittals}
          onChange={v => setFilter('hasSubmittals', v)}
          options={['Yes', 'No']}
          testId={`${testIdPrefix}-filter-submittals`}
          {...dropdownProps('submittals')}
        />
        {/* Toggle item-specific filters */}
        {hasItemFilters && (
          <Button
            variant="ghost"
            size="sm"
            className="h-8 text-xs px-2 text-slate-500"
            onClick={() => setExpanded(e => !e)}
            data-testid={`${testIdPrefix}-filter-toggle`}
          >
            <Filter className="h-3.5 w-3.5 mr-1" />
            More{expanded ? <ChevronUp className="h-3 w-3 ml-1" /> : <ChevronDown className="h-3 w-3 ml-1" />}
          </Button>
        )}
        {/* Clear */}
        {activeCount > 0 && (
          <Button variant="ghost" size="sm" className="h-8 text-xs px-2 text-red-500 hover:text-red-700" onClick={clearFilters} data-testid={`${testIdPrefix}-filter-clear`}>
            <X className="h-3.5 w-3.5 mr-1" />Clear ({activeCount})
          </Button>
        )}
      </div>

      {/* Row 2: Item-specific filters (collapsible) */}
      {expanded && schema.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 pl-0.5">
          {schema.map(f => (
            <FilterSelect
              key={f.field}
              label={f.label}
              value={filters[f.field]}
              onChange={v => setFilter(f.field, v)}
              options={filterOptions[f.field] || []}
              testId={`${testIdPrefix}-filter-${f.field.replace(/\./g, '-')}`}
              {...dropdownProps(f.field)}
            />
          ))}
        </div>
      )}

      {/* Active filter badges */}
      {activeCount > 0 && (
        <div className="flex flex-wrap items-center gap-1.5">
          {Object.entries(filters).filter(([, v]) => v).map(([key, val]) => {
            const label = key === 'search' ? `"${val}"` :
              key === 'countryCode' ? getCountryName(val) :
              key === 'status' ? val.charAt(0).toUpperCase() + val.slice(1) : val;
            const filterLabel = key === 'search' ? 'Search' :
              key === 'manufacturerName' ? 'Manufacturer' :
              key === 'countryCode' ? 'Country' :
              key === 'status' ? 'Status' :
              key === 'hasSubmittals' ? 'Submittals' :
              (schema.find(f => f.field === key)?.label || key);
            return (
              <Badge key={key} variant="secondary" className="text-[10px] gap-1 pr-1 font-normal" data-testid={`${testIdPrefix}-filter-badge-${key}`}>
                {filterLabel}: {label}
                <button onClick={() => setFilter(key, '')} className="ml-0.5 hover:text-red-500 transition-colors">
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            );
          })}
        </div>
      )}
    </div>
  );
};
